import { AudiencesTableProps } from './AudiencesTable.types'
import { DataTable, TableSchema } from 'components/DataTable'
import { SavedAudience } from '@marketing-milk/interfaces'

import { AudienceTools } from 'pages/business/audiences/list-audiences/audience-tools'
import Moment from 'moment'
import React from 'react'
import StarOutlined from '@mui/icons-material/StarOutlined'
import { AudienceStatusIndicator } from 'pages/business/audiences/status-indicator'
import { audienceTypes } from 'pages/business/audiences/list-audiences'
import { generateAudienceSize } from 'pages/business/audiences/list-audiences/const'
import {
  audienceUnavailableMessage,
  isAudienceAvailable,
} from 'pages/business/campaigns/helpers/campaignForm.helpers'

export function AudiencesTable({
  audiences,
  deleteAudience,
  viewAudience,
  recalcAudience,
  editAudience,
  onCheck,
  checkedAudiences,
  onSetDefault,
  markUnavailable,
  emptyMessage,
  hidePagination,
}: AudiencesTableProps) {
  // The row's actions are a star, a pencil and a bin, so a row with none of those
  // handlers has nothing to put in the column at all — the campaign drawer's picker
  // is read-only and would otherwise carry an empty column it has no width for.
  const hasTools = !!(editAudience || deleteAudience || onSetDefault)

  type Columns =
    | 'Audience Name'
    | 'Type'
    | 'Size'
    | 'Last Used'
    | 'Date Created'
    | 'Status'
    | 'Tools'
  const audienceTableSchema: TableSchema<SavedAudience, Columns> = {
    'Audience Name': {
      render: audience => (
        <div className="d-block" style={{ lineHeight: '20px' }}>
          <button
            type="button"
            className="mb-0 block text-left font-medium text-mm-primary-700 hover:underline"
            onClick={() => viewAudience(audience)}
          >
            {/* The star doubles as the set-default control when the row has one, so it
                only appears here on a table whose rows carry no actions. */}
            {!onSetDefault && audience.isDefault ? (
              <StarOutlined fontSize="inherit" className="mr-1 text-mm-primary-600" />
            ) : null}
            {audience.name}
          </button>
          <small className="text-mm-neutral-1-600">
            Updated {Moment.unix(audience.updated_at).fromNow()}
          </small>
          {markUnavailable && !isAudienceAvailable(audience) ? (
            <small className="d-block text-danger">{audienceUnavailableMessage(audience)}</small>
          ) : null}
        </div>
      ),
    },
    Type: {
      render: audience => (
        <span>
          {!audience.type ? (
            <>
              <i className={'fab fa-facebook-f text-inherit mr-2'} />
              Facebook Imported
            </>
          ) : (
            <>
              <i className={`fad fa-${audienceTypes[audience.type].icon} text-inherit mr-2`} />
              {audienceTypes[audience.type].title}
            </>
          )}
        </span>
      ),
    },
    Size: {
      render: audience => generateAudienceSize(audience, () => recalcAudience(audience), false),
    },
    Status: {
      render: audience => <AudienceStatusIndicator status={audience.status} />,
    },
    'Last Used': {
      render: ({ timeLastUsed }) =>
        timeLastUsed ? Moment.unix(timeLastUsed).fromNow() : 'Never Used',
    },
    'Date Created': {
      render: audience =>
        audience.created_at ? Moment.unix(audience.created_at).format('LLL') : 'N/A',
    },
    Tools: {
      render: audience => (
        <AudienceTools
          onEdit={editAudience}
          onDelete={deleteAudience}
          onSetDefault={onSetDefault}
          audience={audience}
        />
      ),
    },
  }

  if (!hasTools) delete (audienceTableSchema as Record<string, unknown>).Tools

  return (
    <DataTable<SavedAudience>
      recordsList={audiences}
      tableSchema={audienceTableSchema}
      emptyMessage={emptyMessage}
      hidePagination={hidePagination}
      hideSearch
      hideColumnFilter
      selectable={{
        setSelectedRows: onCheck,
        selectedRows: checkedAudiences,
      }}
    />
  )
}
